import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminAuthApi } from '../api/adminApi';
import { AdminUser, Profile, LoadingStatus } from '../../types';

interface AdminAuthState {
  admin: AdminUser | null;
  adminProfile: Profile | null;
  isAuthenticated: boolean;
  status: LoadingStatus;
  error: string | null;
  pendingOtpUserId: string | null;
}

const initialState: AdminAuthState = {
  admin: null,
  adminProfile: null,
  isAuthenticated: false,
  status: 'idle',
  error: null,
  pendingOtpUserId: null,
};

export const fetchAdminMe = createAsyncThunk(
  'adminAuth/fetchMe',
  async (_, { rejectWithValue }) => {
    try {
      const res = await adminAuthApi.getMe();
      return res.data;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to fetch profile');
    }
  },
);

export const updateAdminMe = createAsyncThunk(
  'adminAuth/updateMe',
  async (data: Record<string, unknown>, { rejectWithValue }) => {
    try {
      const res = await adminAuthApi.updateMe(data);
      return res.data;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to update profile');
    }
  },
);

export const adminLogin = createAsyncThunk(
  'adminAuth/login',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const res = await adminAuthApi.login(email, password);
      return res.data;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Login failed');
    }
  },
);

export const verifyAdminOtp = createAsyncThunk(
  'adminAuth/verifyOtp',
  async ({ userId, otp }: { userId: string; otp: string }, { rejectWithValue }) => {
    try {
      const res = await adminAuthApi.verifyOtp(userId, otp);
      return res.data;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Invalid or expired code');
    }
  },
);

export const adminLogout = createAsyncThunk('adminAuth/logout', async () => {
  await adminAuthApi.logout();
});

const adminAuthSlice = createSlice({
  name: 'adminAuth',
  initialState,
  reducers: {
    clearAdminError(state) { state.error = null; },
    cancelAdminOtp(state) { state.pendingOtpUserId = null; state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(adminLogin.pending, (state) => { state.status = 'loading'; state.error = null; })
      .addCase(adminLogin.fulfilled, (state, { payload }) => {
        state.pendingOtpUserId = payload.userId;
        state.status = 'succeeded';
      })
      .addCase(adminLogin.rejected, (state, { payload }) => {
        state.status = 'failed';
        state.error = payload as string;
      })
      .addCase(verifyAdminOtp.pending, (state) => { state.status = 'loading'; state.error = null; })
      .addCase(verifyAdminOtp.fulfilled, (state, { payload }) => {
        state.admin = payload.user;
        state.adminProfile = payload.adminProfile ?? null;
        state.isAuthenticated = true;
        state.pendingOtpUserId = null;
        state.status = 'succeeded';
      })
      .addCase(verifyAdminOtp.rejected, (state, { payload }) => {
        state.status = 'failed';
        state.error = payload as string;
      })
      .addCase(adminLogout.fulfilled, (state) => {
        state.admin = null;
        state.adminProfile = null;
        state.isAuthenticated = false;
        state.status = 'idle';
      })
      .addCase(fetchAdminMe.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchAdminMe.fulfilled, (state, { payload }) => {
        if (payload.user) state.admin = payload.user;
        if (payload.adminProfile) state.adminProfile = payload.adminProfile;
        state.isAuthenticated = true;
        state.status = 'succeeded';
      })
      .addCase(fetchAdminMe.rejected, (state) => {
        state.admin = null;
        state.adminProfile = null;
        state.isAuthenticated = false;
        state.status = 'idle';
      })
      .addCase(updateAdminMe.fulfilled, (state, { payload }) => {
        if (payload.user) state.admin = payload.user;
        if (payload.adminProfile) state.adminProfile = payload.adminProfile;
      });
  },
});

export const { clearAdminError, cancelAdminOtp } = adminAuthSlice.actions;
export default adminAuthSlice.reducer;

export const selectAdminUser = (s: { adminAuth: AdminAuthState }) => s.adminAuth.admin;
export const selectAdminProfile = (s: { adminAuth: AdminAuthState }) => s.adminAuth.adminProfile;
export const selectAdminIsAuth = (s: { adminAuth: AdminAuthState }) => s.adminAuth.isAuthenticated;
export const selectAdminAuthStatus = (s: { adminAuth: AdminAuthState }) => s.adminAuth.status;
export const selectAdminAuthError = (s: { adminAuth: AdminAuthState }) => s.adminAuth.error;
export const selectAdminPendingOtpUserId = (s: { adminAuth: AdminAuthState }) => s.adminAuth.pendingOtpUserId;
